/**
 * Line items for a quotation or a purchase order: particular, unit, quantity,
 * unit price, and an amount that works itself out. The total is the sum of the
 * rounded lines, exactly as the printed document will show it.
 */
import { Plus, Trash2 } from 'lucide-react';
import { peso } from '../../lib/format';
import { Button, inputClass } from '../../components/ui';

export interface DraftLine {
  particular: string;
  unit: string;
  qty: string;
  unitPrice: string;
}

export const EMPTY_LINE: DraftLine = { particular: '', unit: '', qty: '1', unitPrice: '' };

/** Kept in step with the server's rule: round each line, then add them up. */
export function lineAmount(line: DraftLine): number {
  const qty = Number(line.qty || '0');
  const price = Number(line.unitPrice || '0');
  if (!Number.isFinite(qty) || !Number.isFinite(price)) return 0;
  return Math.round(qty * price * 100) / 100;
}

export function linesTotal(lines: DraftLine[]): number {
  return lines
    .filter((line) => line.particular.trim() !== '')
    .reduce((total, line) => Math.round((total + lineAmount(line)) * 100) / 100, 0);
}

export function filledLines(lines: DraftLine[]): DraftLine[] {
  return lines.filter((line) => line.particular.trim() !== '');
}

export function LineItemsEditor({
  lines,
  onChange,
  vatIncluded,
  label = 'What we are buying',
}: {
  lines: DraftLine[];
  onChange: (lines: DraftLine[]) => void;
  /** Only changes the wording of the total; the server does the VAT split. */
  vatIncluded: boolean;
  label?: string;
}): JSX.Element {
  const set = (index: number, patch: Partial<DraftLine>): void => {
    onChange(lines.map((line, position) => (position === index ? { ...line, ...patch } : line)));
  };

  const total = linesTotal(lines);

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{label}</span>
        <span className="text-xs text-slate-500">
          One row per item, as the supplier wrote it
        </span>
      </div>

      <div className="space-y-2">
        {lines.map((line, index) => (
          <div
            key={index}
            className="grid grid-cols-12 items-end gap-2 rounded-xl border border-slate-200 p-2 dark:border-slate-700"
          >
            <div className="col-span-12 sm:col-span-5">
              <label className="mb-1 block text-xs text-slate-500">Particular</label>
              <input
                className={inputClass}
                value={line.particular}
                placeholder="e.g. Cat6A cable, 305m box"
                onChange={(event) => set(index, { particular: event.target.value })}
              />
            </div>
            <div className="col-span-4 sm:col-span-2">
              <label className="mb-1 block text-xs text-slate-500">Unit</label>
              <input
                className={inputClass}
                value={line.unit}
                placeholder="box"
                onChange={(event) => set(index, { unit: event.target.value })}
              />
            </div>
            <div className="col-span-4 sm:col-span-1">
              <label className="mb-1 block text-xs text-slate-500">Qty</label>
              <input
                className={inputClass}
                type="number"
                step="0.01"
                min="0.01"
                value={line.qty}
                onChange={(event) => set(index, { qty: event.target.value })}
              />
            </div>
            <div className="col-span-4 sm:col-span-2">
              <label className="mb-1 block text-xs text-slate-500">Unit price</label>
              <input
                className={inputClass}
                type="number"
                step="0.01"
                min="0"
                value={line.unitPrice}
                onChange={(event) => set(index, { unitPrice: event.target.value })}
              />
            </div>
            <div className="col-span-10 sm:col-span-1">
              <label className="mb-1 block text-xs text-slate-500">Amount</label>
              <p className="tabular py-2 text-sm font-semibold">{peso(lineAmount(line))}</p>
            </div>
            <div className="col-span-2 sm:col-span-1 flex justify-end">
              <button
                type="button"
                className="grid h-11 w-11 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-800"
                aria-label={`Remove line ${index + 1}`}
                disabled={lines.length === 1}
                onClick={() => onChange(lines.filter((_, position) => position !== index))}
              >
                <Trash2 aria-hidden="true" className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onChange([...lines, { ...EMPTY_LINE }])}
        >
          <Plus aria-hidden="true" className="h-4 w-4" /> Add line
        </Button>
        <p className="text-sm">
          <span className="text-slate-500">Total </span>
          <span className="tabular font-bold">{peso(total)}</span>
          <span className="text-xs text-slate-500">
            {vatIncluded ? ' VAT inclusive' : ' plus VAT'}
          </span>
        </p>
      </div>
    </div>
  );
}
